import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import sortByString from '../../helpers/helpers';
import { getDayKey } from '../scheduleHelpers';
import ScheduleListShift from '../ScheduleListShift';
import { resourceAxisPropType, resourceRowPropType } from './resourceAxis.propTypes';

const daysInRange = (startDate, endDate) => {
  const count = Math.max(0, Math.round(endDate.diff(startDate, 'days').as('days')));
  return new Array(count).fill(1).map((_, idx) => startDate.plus({ days: idx }));
};

const rangeLabel = (startDate, endDate) => {
  if (startDate.hasSame(endDate, 'day')) return startDate.toFormat('LLL d, yyyy');
  if (startDate.hasSame(endDate, 'year')) {
    return `${startDate.toFormat('LLL d')} – ${endDate.toFormat('LLL d')}`;
  }
  return `${startDate.toFormat('LLL d, yyyy')} – ${endDate.toFormat('LLL d, yyyy')}`;
};

function ResourceList({
  axis,
  rowMap,
  startDate,
  endDate,
  shifts,
  onShiftSelect,
}) {
  const { RowAvatar, shiftField } = axis;

  const { sections, total } = useMemo(() => {
    const days = daysInRange(startDate, endDate);
    const byRow = new Map();
    const seen = new Set();
    const addShift = (key, shift, day) => {
      if (!byRow.has(key)) byRow.set(key, new Map());
      const dayKey = day.toSQLDate();
      const dayMap = byRow.get(key);
      if (!dayMap.has(dayKey)) dayMap.set(dayKey, { day, shifts: [] });
      dayMap.get(dayKey).shifts.push(shift);
    };

    days.forEach((day) => {
      const dayShifts = (shifts[getDayKey(day)] || [])
        .slice()
        .sort((a, b) => a.startTime - b.startTime);
      dayShifts.forEach((shift) => {
        if (shift.id) seen.add(shift.id);
        const shiftRowIds = shift[shiftField] || [];
        if (!shiftRowIds.length) {
          addShift('unassigned', shift, day);
          return;
        }
        shiftRowIds.forEach((rowId) => addShift(rowId, shift, day));
      });
    });

    const named = [];
    byRow.forEach((dayMap, key) => {
      if (key === 'unassigned') return;
      const row = rowMap[key] || { id: key, name: 'Unknown' };
      const dayGroups = Array.from(dayMap.values());
      named.push({ key, row, dayGroups });
    });
    named.sort((a, b) => sortByString('name')(a.row, b.row));

    const nextSections = named;
    if (byRow.has('unassigned')) {
      nextSections.push({
        key: 'unassigned',
        row: null,
        dayGroups: Array.from(byRow.get('unassigned').values()),
      });
    }
    return { sections: nextSections, total: seen.size };
  }, [startDate, endDate, shifts, rowMap, shiftField]);

  return (
    <div className="schedule-list-view">
      <div className="schedule-list-summary">
        <div className="schedule-list-summary-range">{rangeLabel(startDate, endDate)}</div>
        <div className="schedule-list-count">{`${total} shift${total === 1 ? '' : 's'}`}</div>
      </div>
      {sections.map((section) => {
        const shiftCount = section.dayGroups.reduce((sum, group) => sum + group.shifts.length, 0);
        return (
          <section key={section.key} className="schedule-list-user">
            <div className="schedule-list-user-header">
              <div className="schedule-list-user-identity">
                {section.row ? (
                  <RowAvatar row={section.row} className="schedule-list-axis-avatar" />
                ) : (
                  <span className="schedule-list-shift-unassigned">?</span>
                )}
                <span>
                  <span className={`schedule-list-user-name${section.row ? '' : ' schedule-list-user-name-unassigned'}`}>
                    {section.row ? section.row.name : axis.unassignedLabel}
                  </span>
                  {!!section.row?.subtitle && (
                    <span className="schedule-list-user-role">{section.row.subtitle}</span>
                  )}
                </span>
              </div>
              <div className="schedule-list-count">{`${shiftCount} shift${shiftCount === 1 ? '' : 's'}`}</div>
            </div>
            {section.dayGroups.map((group) => (
              <div key={group.day.toSQLDate()} className="schedule-list-user-day">
                <div className="schedule-list-user-day-label">
                  {`${group.day.toFormat('cccc, LLL d')} · `}
                  <span className="schedule-list-shift-count">
                    {`${group.shifts.length} shift${group.shifts.length === 1 ? '' : 's'}`}
                  </span>
                </div>
                {group.shifts.map((shift) => (
                  <ScheduleListShift
                    key={`${section.key}-${shift.id}`}
                    shift={shift}
                    onSelect={onShiftSelect}
                  />
                ))}
              </div>
            ))}
          </section>
        );
      })}
      {total === 0 && <div className="schedule-list-empty">No shifts</div>}
    </div>
  );
}

/* eslint-disable react/forbid-prop-types */
ResourceList.propTypes = {
  axis: resourceAxisPropType.isRequired,
  rowMap: PropTypes.objectOf(resourceRowPropType).isRequired,
  startDate: PropTypes.object.isRequired,
  endDate: PropTypes.object.isRequired,
  shifts: PropTypes.object.isRequired,
  onShiftSelect: PropTypes.func.isRequired,
};

export default ResourceList;
